/**
 * auth.ts — LLM bearer token auth with hash-only persistence.
 *
 * The plaintext token lives in memory only (module-level var).
 * localStorage stores only the SHA-256 hash — never the plaintext.
 * This means someone inspecting localStorage sees a hash, not the password.
 *
 * Flow:
 *  - First use: user types password → hash stored, plaintext in memory → requests proceed.
 *  - New page load: hash found → prompt "re-enter password" → compare hash → restore memory.
 *  - Wrong password: hash mismatch → show error (no server round-trip needed).
 *  - 401 from server: clear hash + memory, re-show gate (password was wrong or revoked).
 *
 * Dev: gate never shown; Vite proxy handles auth server-side.
 */

const HASH_KEY = '312-finance-llm-hash'
const IS_DEV = import.meta.env.DEV

// Plaintext token in JS memory only — never written to any storage.
let _token = ''

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

/** Returns the bearer token for API requests, or '' if not set. */
export function getLLMToken(): string {
  if (IS_DEV) return ''
  return _token
}

/** Store the token: hash goes to localStorage, plaintext stays in memory. */
export async function setLLMToken(token: string): Promise<void> {
  _token = token
  localStorage.setItem(HASH_KEY, await sha256hex(token))
}

/** Clear token from memory and hash from localStorage (e.g. on 401). */
export function clearLLMToken(): void {
  _token = ''
  localStorage.removeItem(HASH_KEY)
}

/** True when we need to show the password gate. */
export function needsToken(): boolean {
  if (IS_DEV) return false
  return !_token
}

/** True when user has previously authenticated (hash persisted). */
export function hasPreviousHash(): boolean {
  if (IS_DEV) return false
  return !!localStorage.getItem(HASH_KEY)
}

/** Verify a typed password against the stored hash without trusting the server. */
export async function verifyToken(token: string): Promise<boolean> {
  const stored = localStorage.getItem(HASH_KEY)
  if (!stored) return true  // No hash yet — accept anything (server will 401 if wrong)
  return (await sha256hex(token)) === stored
}

